import NiceModal, { useModal } from '@ebay/nice-modal-react';
import {
  Button,
  Group,
  Modal,
  MultiSelect,
  PasswordInput,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMemo } from 'react';

import { useCreateAccount } from '../api/createAccount';
import { useGetAccounts } from '../api/getAccounts';
import { useSetAccountRoles } from '../api/setAccountRoles';
import { accountTypes } from '../types';

import { useGetRoles } from '@/features/roles';

type CreateProps = {
  account?: undefined;
};

type EditProps = {
  account: accountTypes.Account;
};

type Props = CreateProps | EditProps;

export const AccountModal = NiceModal.create(({ account }: Props) => {
  const modal = useModal();
  const isEdit = !!account;

  const { data: rolesData } = useGetRoles();
  const { data: accountsData } = useGetAccounts({ config: { enabled: isEdit } });

  const existingRoleIdSet = new Set(rolesData?.roles.map((role) => role._id) ?? []);
  const filteredRoleIds = (account?.roleIds ?? []).filter((id) => existingRoleIdSet.has(id));

  const form = useForm({
    initialValues: {
      username: account?.username ?? '',
      password: '',
      roleIds: filteredRoleIds,
    },
    validate: {
      username: (value) => (value.trim() ? null : 'Username is required'),
      password: (value) => (!isEdit && !value.trim() ? 'Password is required' : null),
    },
  });

  const lockedRoleIds = useMemo(() => {
    if (!isEdit || !rolesData || !accountsData) {
      return new Set<string>();
    }
    const wildcardRoleIds = rolesData.roles
      .filter((role) => role.permissions.includes('*'))
      .map((role) => role._id);
    return new Set(
      wildcardRoleIds.filter((roleId) => {
        if (!(account.roleIds ?? []).includes(roleId)) {
          return false;
        }
        const otherHolders = accountsData.accounts.filter(
          (acc) => acc._id !== account._id && (acc.roleIds ?? []).includes(roleId)
        );
        return otherHolders.length === 0;
      })
    );
  }, [isEdit, rolesData, accountsData, account]);

  const roleOptions = (rolesData?.roles ?? []).map((role) => ({
    value: role._id,
    label: role.name,
    disabled: lockedRoleIds.has(role._id),
  }));

  const lockedRoleNames = (rolesData?.roles ?? [])
    .filter((role) => lockedRoleIds.has(role._id))
    .map((role) => role.name);

  const createAccount = useCreateAccount({ config: { onSuccess: modal.remove } });
  const setRoles = useSetAccountRoles({ config: { onSuccess: modal.remove } });
  const isLoading = createAccount.isLoading || setRoles.isLoading;

  const handleSubmit = form.onSubmit(({ username, password, roleIds }) => {
    if (isEdit) {
      setRoles.mutate({ id: account._id, roleIds });
    } else {
      createAccount.mutate({ username, password, roleIds });
    }
  });

  return (
    <Modal
      centered
      size="md"
      overlayProps={{ opacity: 0.4, blur: 1.5 }}
      opened={modal.visible}
      onClose={modal.remove}
      title={isEdit ? 'Edit account roles' : 'New account'}
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="sm">
          <TextInput
            required
            label="Username"
            placeholder="username"
            disabled={isEdit}
            {...form.getInputProps('username')}
          />
          {!isEdit && (
            <PasswordInput
              required
              label="Password"
              placeholder="Password"
              {...form.getInputProps('password')}
            />
          )}
          <MultiSelect
            label="Roles"
            placeholder="Select roles"
            data={roleOptions}
            {...form.getInputProps('roleIds')}
            onChange={(newIds) => {
              const removedIds = form.values.roleIds.filter((id) => !newIds.includes(id));
              if (removedIds.some((id) => lockedRoleIds.has(id))) {
                return;
              }
              form.setFieldValue('roleIds', newIds);
            }}
          />
          {lockedRoleNames.length > 0 && (
            <Text size="xs" c="dimmed">
              {lockedRoleNames.length === 1 ? 'Role' : 'Roles'} {lockedRoleNames.join(', ')} cannot
              be removed — this is the last account with superadmin access.
            </Text>
          )}
          <Group justify="right" mt="md">
            <Button variant="outline" onClick={modal.remove} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" loading={isLoading}>
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
});
